<template>
  <div>

    <!-- XOL-464: hoisted to the component root, above every other branch
         (including `errored` / `isEmpty`). A strict-position violation can
         legitimately emit ZERO schedule rows, which used to land this panel
         on `isEmpty` and show "No fee schedule — this sharer has no rate set
         yet." — wrong copy, and no explanation at all, since the old
         placement inside `v-else v-loading` never rendered on that branch.
         Costs nothing in `errored` (`meta` is null there, so this is [] and
         renders nothing) or in the happy path (still []). -->
    <sharer-billing-issues :issues="billingIssues" />

    <p
      v-if="rows.length > 0"
      class="text-muted schedule-caption"
    >Monthly fee per tier, measured in elapsed months from the sharer's Agreement Start{{ started ? ' (' + formatDate(started) + ')' : '' }} — not calendar dates and not handover. Each row is that tier's own monthly rate, not a running total.</p>

    <el-alert
      v-if="errored"
      type="error"
      :closable="false"
      show-icon
      title="Couldn't load the fee schedule"
      description="Please try again in a moment." />

    <el-row v-else-if="isEmpty">
      <el-col :span="24" align="center">
        <i class="fa fa-calendar-times text-muted"></i> <i class="text-muted">{{ emptyText }}</i>
      </el-col>
    </el-row>

    <div v-else v-loading="loading">

      <!-- XOL-416: the schedule renders its TIER BANDS with `fee: null` when the
           Sharer # is unassigned — a populated table, not an empty panel — so
           `emptyText` never fires and the user would otherwise see a column of
           dashes with no explanation. The sentence is the API's, verbatim, and is
           deliberately NOT the fee timeline's: there a month priced 0 WAS due,
           here a band's price is simply unknowable. -->
      <el-alert
        v-if="isUnpriced"
        type="error"
        :closable="false"
        show-icon
        title="No amount can be calculated for this sharer"
        :description="unpricedReason" />

      <!-- Summary strip — only when meta is present -->
      <el-row v-if="metaSummary" :gutter="16" class="schedule-summary">
        <el-col :xs="24" :sm="8" class="schedule-summary__stat">
          <div class="schedule-summary__label">Current month</div>
          <div class="schedule-summary__value">
            {{ metaSummary.monthLabel }}
            <!-- XOL-482/F2: this used to branch on `anchor === 'handover'`. It
                 cannot be. XOL-466 removed handover as an anchor source, and the
                 API's anchorSourceForLicensee() is now literally
                 `normalize($lic->started) !== null ? 'started' : null` — so the
                 field is 'started' | null and the handover arm was unreachable
                 copy. The `v-if` still reads the STRING rather than a boolean:
                 the API deliberately kept `anchor` a nullable string so that
                 adding a second anchor later is a deliberate widening here, not
                 a silent one. Don't collapse it to a flag. -->
            <el-tooltip
              v-if="metaSummary.anchor"
              content="Measured in elapsed months from the sharer's agreement start date."
              placement="top">
              <i class="fa fa-info-circle text-muted" aria-label="How the elapsed month is measured"></i>
            </el-tooltip>
          </div>
        </el-col>
        <el-col :xs="24" :sm="8" class="schedule-summary__stat">
          <div class="schedule-summary__label">Active tier</div>
          <div class="schedule-summary__value">
            <el-tag
              v-if="metaSummary.activeTier != null"
              size="small"
              effect="light"
              type="success">T{{ metaSummary.activeTier }}</el-tag>
            <span v-else class="text-muted">{{ metaSummary.activeTierLabel }}</span>
          </div>
        </el-col>
        <el-col :xs="24" :sm="8" class="schedule-summary__stat">
          <div class="schedule-summary__label">Billing</div>
          <div class="schedule-summary__value">
            {{ metaSummary.modeLabel }}
            <div v-if="metaSummary.showPosition" class="schedule-summary__position">{{ metaSummary.positionLabel }}</div>
          </div>
        </el-col>
      </el-row>

      <el-table :data="displayRows" style="width:100%" :row-class-name="rowClassName">

        <el-table-column label="Tier" width="64" align="center">
          <template slot-scope="scope">
            <span
              class="tier-tag"
              :class="tierBadgeClass(scope.row.tierSeq)"
              :aria-label="tierBadgeClass(scope.row.tierSeq) === 'tier-1' ? 'T' + scope.row.tierSeq + ', current tier' : undefined">{{ tierLabel(scope.row.tierSeq) }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Period" min-width="120">
          <template slot-scope="scope">
            <el-tag
              v-if="isActiveRow(scope.row.tierSeq)"
              size="mini"
              type="success"
              effect="light">Current</el-tag>
            {{ scope.row.periodLabel }}
          </template>
        </el-table-column>

        <el-table-column min-width="150">
          <template #header>
            <span>Months (elapsed) <el-tooltip placement="top" content="Elapsed months since the sharer's start date. The end month is exclusive — this tier applies up to, but not including, the end month."><i class="fa fa-info-circle text-muted" aria-label="Month range explanation"></i></el-tooltip></span>
          </template>
          <template slot-scope="scope">
            {{ scope.row.monthLabel }}
          </template>
        </el-table-column>

        <el-table-column label="Monthly Fee" min-width="120" align="right" class-name="fee-cell">
          <template slot-scope="scope">
            {{ scope.row.feeLabel }}
          </template>
        </el-table-column>

      </el-table>
    </div>
  </div>
</template>

<script>
import NumberMixins from '@/mixins/NumberMixins'
import PostMixins from '@/mixins/PostMixins'
import DateMixins from '@/mixins/DateMixins'

import SharerBillingIssues from './SharerBillingIssues'

export default {
  name: 'sharer-fee-schedule',

  mixins: [NumberMixins, PostMixins, DateMixins],

  components: {
    SharerBillingIssues
  },

  props: {
    id: {
      type: [Number, String],
      required: true
    },
    started: {
      type: String,
      default: null
    }
  },

  data: function () {
    return {
      rows: [],
      meta: null,
      loading: false,
      loaded: false,
      errored: false
    }
  },

  computed: {
    displayRows: function () {
      var self = this
      return self.rows.map(function (row) {
        var periodLabel
        if (row.fromYear === row.toYear) {
          periodLabel = 'Year ' + row.fromYear
        } else {
          periodLabel = 'Year ' + row.fromYear + '–' + row.toYear
        }

        var monthLabel = row.fromMonth + '–' + row.toMonth

        var feeLabel
        if (row.fee === null || row.fee === undefined) {
          feeLabel = '—'
        } else {
          feeLabel = self.formatMoney(row.fee, 2)
        }

        return {
          tierSeq: row.tierSeq,
          periodLabel: periodLabel,
          monthLabel: monthLabel,
          feeLabel: feeLabel
        }
      })
    },

    isEmpty: function () {
      return this.loaded && !this.errored && this.rows.length === 0
    },

    // XOL-416: the API tells us WHY the schedule is empty via `meta.reasonCode`
    // — a machine-readable sibling of the human `meta.reason` prose. Switch on
    // the code, never on the prose (it is a literal sentence that changes with
    // any copy edit). The default stays the historical wording, which is only
    // correct for the rateless case.
    emptyText: function () {
      var code = this.meta && this.meta.reasonCode

      // NOTE: `unassigned_sharer_no` is deliberately NOT handled here. Sharer # 0
      // means the By-No rate cannot be PRICED, not that the sharer is unbillable —
      // the API returns a POPULATED schedule (tier bands with `fee: null`) with the
      // code set, so that case is a banner over real rows, not an empty state.
      // Measured 2026-08-15 on the fixture: rows 1, fee null. Re-adding a branch
      // here would render copy that could only ever fire on a body the API no
      // longer sends. See `isUnpriced` and the banner in the template.

      // Prefer the API's own sentence when it sends one. XOL-416 added
      // `meta.reason` so a state's copy has ONE source rather than a server
      // sentence and a client paraphrase drifting apart. The strings below stay
      // as the fallback: `meta.reason` is null when there is nothing to explain,
      // and dropping working copy for a blank panel would be a worse trade.
      if (this.meta && this.meta.reason) {
        return this.meta.reason
      }

      if (code === 'no_billing_start') {
        return 'No fee schedule — this sharer has no Billing Start or Agreement Start date.'
      }

      return 'No fee schedule — this sharer has no rate set yet.'
    },

    // XOL-416: same gate as the fee timeline's — keyed on the reason code, NEVER on
    // the sharer's number. A By-Qty sharer at #0 prices normally and must not see
    // any of this.
    isUnpriced: function () {
      return !!(this.meta && this.meta.reasonCode === 'unassigned_sharer_no')
    },

    // XOL-462: strict_position_out_of_range / strict_term_exceeded, absent on
    // any API not carrying the field yet — never render "no issues" from a
    // missing key or a failed fetch.
    billingIssues: function () {
      return (this.meta && Array.isArray(this.meta.issues)) ? this.meta.issues : []
    },

    // The banner's body. `meta.reason` is the API's own sentence and is preferred
    // whenever it is there — but the gate above is `reasonCode`, and reasonCode
    // SHIPPED BEFORE reason did. So an API carrying the code without the prose is
    // a real, reachable combination: it is exactly what the deployed API looks like
    // until the paired change lands, and if the two halves land out of order this
    // is the branch that runs. Binding `meta.reason` straight into the alert would
    // render a titled banner with an empty body in that window — an error message
    // that does not say what is wrong. The fallback keeps it self-explanatory.
    unpricedReason: function () {
      if (this.meta && this.meta.reason) {
        return this.meta.reason
      }
      // XOL-626 (D11): a finance or project manager can set the number too, not only a super.
      return 'Sharer # is 0 (unassigned), so the By-Sharer-No rate cannot be resolved — the tier ' +
        'structure is shown but no amount can be calculated. A finance or project manager, or a ' +
        'super user, must set a number from 1 to 99 on the sharer\'s Project tab.'
    },

    activeTierSeq: function () {
      return this.meta && this.meta.activeTierSeq != null ? this.meta.activeTierSeq : null
    },

    hasActive: function () {
      return this.activeTierSeq != null
    },

    metaSummary: function () {
      if (!this.meta) {
        return null
      }

      var self = this
      var meta = self.meta
      var overridden = !!meta.overridden

      var monthLabel = meta.elapsedMonth != null ? String(meta.elapsedMonth) : 'Not started yet'

      var activeTier = self.activeTierSeq
      var activeTierLabel
      if (overridden) {
        // XOL-355: an overridden sharer bills a flat rate — no tier is "active".
        // Render the em-dash the Fee Timeline / ledger uses for null fields.
        activeTierLabel = '—'
      } else if (activeTier != null) {
        activeTierLabel = 'T' + activeTier
      } else {
        activeTierLabel = '—'
      }

      var modeLabel = self.billingModeLabel(meta.billingMode)
      var isQty = !overridden && String(meta.billingMode).toLowerCase() === 'qty'

      var positionLabel
      var showPosition
      if (overridden) {
        // XOL-355: drop the by-sharer Position/Count line; surface the flat
        // override rate instead when the backend supplies it.
        showPosition = meta.overrideRate != null
        positionLabel = meta.overrideRate != null ? 'Override rate: ' + self.formatMoney(meta.overrideRate, 2) : ''
      } else {
        showPosition = true
        var rawPos = meta.position
        var billedPos = meta.billedPosition
        var posValue = rawPos != null ? String(rawPos) : '—'
        // XOL-376 Inc 2: when the billed (post-clamp) position differs from the raw
        // position, surface the clamp inline. Equal / null billedPosition (override,
        // no rate) renders exactly as before.
        if (billedPos != null && rawPos != null && billedPos !== rawPos) {
          posValue = posValue + ' · billed at #' + billedPos
        }
        if (isQty) {
          positionLabel = 'Count: ' + posValue
        } else {
          positionLabel = 'Position: ' + posValue
        }
      }

      return {
        overridden: overridden,
        monthLabel: monthLabel,
        anchor: meta.anchor || null,
        activeTier: activeTier,
        activeTierLabel: activeTierLabel,
        modeLabel: modeLabel,
        isQty: isQty,
        showPosition: showPosition,
        positionLabel: positionLabel
      }
    }
  },

  created: function () {
    this.load()
  },

  methods: {
    load: function () {
      if (!this.id) {
        this.loaded = true
        return
      }

      var self = this
      this.loading = true
      this.errored = false
      this.loaded = false

      this.fetchData(
        'licensees/' + this.id + '/fee-schedule',
        function (data) {
          if (Array.isArray(data)) {
            self.rows = data
            self.meta = null
          } else if (data && typeof data === 'object') {
            self.rows = Array.isArray(data.rows) ? data.rows : []
            self.meta = (data.meta && typeof data.meta === 'object') ? data.meta : null
          } else {
            self.rows = []
            self.meta = null
          }
          self.loading = false
          self.loaded = true
        },
        'fee schedule',
        function (_err) {
          self.errored = true
          self.loading = false
          self.loaded = true
          // XOL-464: a REFETCH (not just a first load) can fail after a prior
          // success already populated `meta.issues`. Without this, the hoisted
          // <sharer-billing-issues> banner (root-level, unconditional on
          // `errored`) would keep asserting a specific billing diagnosis —
          // e.g. "This sharer is outside the rate's sharer range" — sourced
          // from data we just failed to refresh, directly above the
          // "Couldn't load the fee schedule" alert admitting we don't have
          // current data. Clear the state at its source, the same call made
          // for `fromSharerError` / the `strictPosition` watcher elsewhere in
          // this ticket (watcher deleted in XOL-487) — never gate a stale
          // value in the template when it can be cleared where it lives.
          self.meta = null
        }
      )
    },

    // XOL-355: single source of truth for "is this row the active tier". The
    // hasActive guard stops the override flat row (tierSeq null) from matching
    // activeTierSeq null — null === null would otherwise read as active and
    // wrongly tint the row / show the Current tag.
    isActiveRow: function (tierSeq) {
      return this.hasActive && tierSeq === this.activeTierSeq
    },

    rowClassName: function (obj) {
      return this.isActiveRow(obj.row.tierSeq) ? 'tier-row--active' : ''
    },

    tierBadgeClass: function (tierSeq) {
      return this.isActiveRow(tierSeq) ? 'tier-1' : 'tier-n'
    },

    // XOL-355: the override flat row carries tierSeq null — render an em-dash
    // instead of a bare "T".
    tierLabel: function (tierSeq) {
      return tierSeq != null ? 'T' + tierSeq : '—'
    },

    billingModeLabel: function (mode) {
      var m = String(mode).toLowerCase()
      if (m === 'overridden') {
        return 'Rate overridden'
      }
      return m === 'qty' ? 'Billed by sharer Qty' : 'Billed by sharer No.'
    }
  }
}
</script>

<style scoped>
/* Tier tag tokens — copied from RateTiersCell.vue */
.tier-tag {
  display: inline-block;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
}

.tier-1 {
  background: #f0f9eb;
  color: #67c23a;
}

.tier-n {
  background: #f4f4f5;
  color: #909399;
}

::v-deep .fee-cell {
  font-variant-numeric: tabular-nums;
}

.schedule-caption {
  font-size: 12px;
}

/* Active row highlight */
::v-deep .tier-row--active td {
  background: #f0f9eb;
}

/* Summary strip */
.schedule-summary {
  margin-bottom: 8px;
}

.schedule-summary__stat {
  padding: 8px 0;
}

.schedule-summary__label {
  font-size: 12px;
  color: #606266;
  margin-bottom: 2px;
}

.schedule-summary__value {
  font-size: 16px;
  font-variant-numeric: tabular-nums;
}

.schedule-summary__position {
  font-size: 12px;
  color: #909399;
}
</style>
